import React, { useState, useEffect, useMemo } from "react";
import { compose } from "recompose";
import { withFirebase } from "../../../Firebase";
import "./index.css";

const SUGGESTION_LABELS = {
  more_examples: "More examples",
  bullet_help: "Help writing bullets",
  spell_check: "Spell / grammar check",
  cover_letter: "Cover letter builder",
  more_templates: "More templates",
  multiple_versions: "Save multiple versions",
  mobile: "Easier on phone",
  other: "Something else",
};

const npsCategory = (score) => {
  if (score <= 6) return "detractor";
  if (score <= 8) return "passive";
  return "promoter";
};

const formatDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ResumeFeedback = ({ firebase }) => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    firebase
      .resumeBuilderFeedback()
      .orderBy("submittedAt", "desc")
      .get()
      .then((snap) => {
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setFeedback(rows);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.log("ResumeFeedback load error", err);
        setError("Could not load feedback. Try refreshing.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firebase]);

  const stats = useMemo(() => {
    if (!feedback.length) {
      return {
        total: 0,
        avgNps: 0,
        npsScore: 0,
        promoters: 0,
        passives: 0,
        detractors: 0,
        distribution: Array(11).fill(0),
        suggestions: [],
      };
    }

    const total = feedback.length;
    const sum = feedback.reduce((s, f) => s + (f.nps || 0), 0);
    const avgNps = sum / total;

    let promoters = 0;
    let passives = 0;
    let detractors = 0;
    const distribution = Array(11).fill(0);

    feedback.forEach((f) => {
      const score = f.nps;
      if (typeof score === "number" && score >= 0 && score <= 10) {
        distribution[score]++;
        const cat = npsCategory(score);
        if (cat === "promoter") promoters++;
        else if (cat === "passive") passives++;
        else detractors++;
      }
    });

    const npsScore = Math.round(((promoters - detractors) / total) * 100);

    const suggestionCounts = {};
    feedback.forEach((f) => {
      (f.suggestions || []).forEach((s) => {
        suggestionCounts[s] = (suggestionCounts[s] || 0) + 1;
      });
    });
    const suggestions = Object.entries(suggestionCounts)
      .map(([id, count]) => ({
        id,
        label: SUGGESTION_LABELS[id] || id,
        count,
        pct: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.count - a.count);

    return {
      total,
      avgNps,
      npsScore,
      promoters,
      passives,
      detractors,
      distribution,
      suggestions,
    };
  }, [feedback]);

  const maxDistribution = Math.max(1, ...stats.distribution);
  const maxSuggestion = stats.suggestions[0]?.count || 1;
  const recentWithComments = feedback.filter((f) => f.comment?.trim());

  if (loading) {
    return (
      <div className="resume-feedback__loading">Loading feedback…</div>
    );
  }

  if (error) {
    return <div className="resume-feedback__error">{error}</div>;
  }

  if (!feedback.length) {
    return (
      <div className="resume-feedback__empty">
        <h3>No feedback yet</h3>
        <p>
          Once participants finish the Resume Builder and download their
          resume, their feedback will show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="resume-feedback">
      <div className="resume-feedback__cards">
        <div className="resume-feedback__card">
          <div className="resume-feedback__card-label">Total responses</div>
          <div className="resume-feedback__card-value">{stats.total}</div>
        </div>
        <div className="resume-feedback__card">
          <div className="resume-feedback__card-label">NPS score</div>
          <div
            className={`resume-feedback__card-value resume-feedback__card-value--${
              stats.npsScore >= 50
                ? "promoter"
                : stats.npsScore >= 0
                ? "passive"
                : "detractor"
            }`}
          >
            {stats.npsScore > 0 ? "+" : ""}
            {stats.npsScore}
          </div>
          <div className="resume-feedback__card-sub">
            % Promoters − % Detractors
          </div>
        </div>
        <div className="resume-feedback__card">
          <div className="resume-feedback__card-label">Average rating</div>
          <div className="resume-feedback__card-value">
            {stats.avgNps.toFixed(1)}
            <span className="resume-feedback__card-unit"> / 10</span>
          </div>
        </div>
        <div className="resume-feedback__card resume-feedback__card--breakdown">
          <div className="resume-feedback__card-label">Breakdown</div>
          <div className="resume-feedback__breakdown">
            <div className="resume-feedback__breakdown-row">
              <span className="resume-feedback__dot resume-feedback__dot--promoter" />
              Promoters (9-10)
              <span className="resume-feedback__breakdown-count">
                {stats.promoters}
              </span>
            </div>
            <div className="resume-feedback__breakdown-row">
              <span className="resume-feedback__dot resume-feedback__dot--passive" />
              Passives (7-8)
              <span className="resume-feedback__breakdown-count">
                {stats.passives}
              </span>
            </div>
            <div className="resume-feedback__breakdown-row">
              <span className="resume-feedback__dot resume-feedback__dot--detractor" />
              Detractors (0-6)
              <span className="resume-feedback__breakdown-count">
                {stats.detractors}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="resume-feedback__section">
        <h3 className="resume-feedback__section-title">Score distribution</h3>
        <div className="resume-feedback__distribution">
          {stats.distribution.map((count, score) => (
            <div key={score} className="resume-feedback__dist-col">
              <div className="resume-feedback__dist-count">{count}</div>
              <div className="resume-feedback__dist-bar-wrap">
                <div
                  className={`resume-feedback__dist-bar resume-feedback__dist-bar--${npsCategory(
                    score
                  )}`}
                  style={{
                    height: `${(count / maxDistribution) * 100}%`,
                  }}
                />
              </div>
              <div className="resume-feedback__dist-label">{score}</div>
            </div>
          ))}
        </div>
      </div>

      {stats.suggestions.length ? (
        <div className="resume-feedback__section">
          <h3 className="resume-feedback__section-title">
            What participants want
          </h3>
          <div className="resume-feedback__suggestions">
            {stats.suggestions.map((s) => (
              <div key={s.id} className="resume-feedback__suggestion-row">
                <div className="resume-feedback__suggestion-label">
                  {s.label}
                </div>
                <div className="resume-feedback__suggestion-bar-wrap">
                  <div
                    className="resume-feedback__suggestion-bar"
                    style={{ width: `${(s.count / maxSuggestion) * 100}%` }}
                  />
                </div>
                <div className="resume-feedback__suggestion-count">
                  {s.count} <span>({s.pct}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {recentWithComments.length ? (
        <div className="resume-feedback__section">
          <h3 className="resume-feedback__section-title">
            Recent comments ({recentWithComments.length})
          </h3>
          <div className="resume-feedback__comments">
            {recentWithComments.map((f) => (
              <div key={f.id} className="resume-feedback__comment">
                <div className="resume-feedback__comment-header">
                  <span
                    className={`resume-feedback__comment-score resume-feedback__comment-score--${npsCategory(
                      f.nps
                    )}`}
                  >
                    {f.nps}
                  </span>
                  <span className="resume-feedback__comment-meta">
                    {f.displayName || f.email || "Anonymous"}
                  </span>
                  <span className="resume-feedback__comment-date">
                    {formatDate(f.submittedAt)}
                  </span>
                </div>
                <div className="resume-feedback__comment-text">
                  {f.comment}
                </div>
                {(f.suggestions || []).length ? (
                  <div className="resume-feedback__comment-chips">
                    {f.suggestions.map((sid) => (
                      <span
                        key={sid}
                        className="resume-feedback__comment-chip"
                      >
                        {SUGGESTION_LABELS[sid] || sid}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default compose(withFirebase)(ResumeFeedback);
